import React from "react";
import { Link, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import {
    getReplacedByPartNumber,
    getReplacementPartNumber,
    getReplacementProductRoute,
} from "../../utilities/replacementProduct";

/**
 * Caption shown under a part number when it has a replacement ("Replacement Part: X") and/or is
 * itself a replacement ("Replacement for Part: Y").
 *
 * @param {Object} props
 * @param {Object} props.product Product row, or an order line carrying nested `productsData`.
 * @param {string} [props.component="span"] Typography root element; "div" renders block-level.
 * @param {boolean} [props.reserveSpace=false] Render a blank line instead of null so grid rows keep equal height.
 * @param {Object} [props.sx] Extra MUI sx merged onto the caption.
 * @param {boolean} [props.linkToReplacement=false] Render the replacement number as a link to its
 *   detail page when the target product is known (see getReplacementProductRoute). Opt-in because
 *   this caption also sits inside clickable cards, where a nested link would fight the card click.
 * @returns {JSX.Element|null} The caption, or null when there is nothing to say and no reserved space.
 */
const ReplacementPartNotice = ({
    product,
    component = "span",
    reserveSpace = false,
    sx = {},
    linkToReplacement = false,
}) => {
    const navigate = useNavigate();
    const replacementPartNumber = getReplacementPartNumber(product);
    const replacedPartNumber = getReplacedByPartNumber(product);
    if (!replacementPartNumber && !replacedPartNumber && !reserveSpace) return null;

    const route = linkToReplacement ? getReplacementProductRoute(product) : "";
    const replacementLabel = route ? (
        <Link
            underline="hover"
            color="inherit"
            sx={{ cursor: "pointer" }}
            onClick={(event) => {
                event.stopPropagation();
                navigate(route);
            }}>
            {replacementPartNumber}
        </Link>
    ) : (
        replacementPartNumber
    );

    return (
        <Typography
            component={component}
            variant="caption"
            color="warning.dark"
            sx={{
                display: component === "span" ? "inline" : "block",
                fontWeight: 600,
                lineHeight: 1.25,
                fontSize: "11px",
                ...sx,
            }}
        >
            {replacementPartNumber ? <>Replacement Part: {replacementLabel}</> : null}
            {replacementPartNumber && replacedPartNumber ? " | " : null}
            {replacedPartNumber ? `Replacement for Part: ${replacedPartNumber}` : null}
            {!replacementPartNumber && !replacedPartNumber ? "\u00A0" : null}
        </Typography>
    );
};

export default ReplacementPartNotice;
